/**
 * Manages the documents table data — generation from order/auth data,
 * jcodes memo, row updates with UserStore persistence, and unmount cleanup.
 * @module useDocumentTableState
 */

import { useEffect, useMemo, useState } from "react";
import { Authorization } from "../../../../../data-model/AuthorizationModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { UserGeneralInfoContextKeys } from "../../../../../enums/userGeneralInfoContextKeys";
import { UserStore } from "../../../../../store/UserGeneralInfoStore";
import { getAllJCodesFromAuthOnFile } from "../../../../singleOrderLayout/utils/existingDocsToLs";
import { generateDocumentsTableData } from "../documentsTable";

/**
 * Inputs for {@link useDocumentTableState}: route order id and both wrapped
 * and unwrapped general/auth payloads from the single-order data accumulator.
 */
interface UseDocumentTableStateParams {
  orderId: string | undefined;
  generalOrderDetails: { order: GeneralOrderDetails } | null;
  unwrappedGeneralOrderDetails: GeneralOrderDetails | null;
  unwrappedAuthOnFileDetails: AuthOnFile | null;
  authOnFileDetails: { auth_on_file: AuthOnFile } | null;
  authorizationData: Authorization | null;
  authorizationDataLoading: boolean;
}

/**
 * Encapsulates document table data lifecycle: generation from order context,
 * jcodes extraction, row-level updates, and UserStore cleanup on unmount.
 *
 * @param params - Order and auth data used to build the table.
 * @returns Table data, flattened jcodes, and the row update handler.
 */
export const useDocumentTableState = ({
  orderId,
  generalOrderDetails,
  unwrappedGeneralOrderDetails,
  unwrappedAuthOnFileDetails,
  authOnFileDetails,
  authorizationData,
  authorizationDataLoading,
}: UseDocumentTableStateParams) => {
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    return () => {
      UserStore.removeItem(UserGeneralInfoContextKeys.MEDICINE_NAMES);
    };
  }, []);

  useEffect(() => {
    if (generalOrderDetails) {
      const generatedData = generateDocumentsTableData(
        unwrappedGeneralOrderDetails as GeneralOrderDetails,
        unwrappedAuthOnFileDetails as AuthOnFile,
        authorizationData,
        authorizationDataLoading ?? false,
      );
      setTableData(generatedData);
    }
  }, [generalOrderDetails, authOnFileDetails, orderId]);

  const jcodes = useMemo(
    () => getAllJCodesFromAuthOnFile(unwrappedAuthOnFileDetails as AuthOnFile),
    [unwrappedAuthOnFileDetails],
  );

  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    setTableData((prevData) => {
      const updatedData = [...prevData];
      if (updatedData[rowIndex]) {
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          [fieldKey]: newValue,
        };
      }

      UserStore.setItem(
        `${UserGeneralInfoContextKeys.CPT_CODES}_${orderId}`,
        updatedData,
      );

      return updatedData;
    });
  };

  return { tableData, jcodes, handleTableRowUpdate };
};
